import type { VideoCostProfile } from "@/libs/pricing";

type ProfileKey = VideoCostProfile["key"];

export interface PricingProductionCopy {
  homeTitle: string;
  costLabel: string;
  outputLabel: string;
  tableTitle: string;
  tableDescription: string;
  profileLabel: string;
  creditsLabel: string;
  creditsUnit: string;
  minutesShort: string;
  visualsLabel: string;
  estimateNote: string;
  fallbackNote: string;
  profiles: Record<ProfileKey, string>;
}

const copies: Record<string, PricingProductionCopy> = {
  en: {
    homeTitle: "Plan with four common video formats",
    costLabel: "estimated credits",
    outputLabel: "estimated videos",
    tableTitle: "Estimated video output by plan",
    tableDescription: "Use these ranges to compare plans against a repeatable production format.",
    profileLabel: "Production profile",
    creditsLabel: "Credits per video",
    creditsUnit: "credits",
    minutesShort: "min",
    visualsLabel: "visuals",
    estimateNote: "Voice model, visual model, retries, and scene density change actual credit use.",
    fallbackNote: "Unit costs are temporarily using clearly marked fallback values because live pricing configuration was unavailable.",
    profiles: { short: "Short faceless video", explainer: "Educational explainer", "long-form": "Long-form faceless video", podcast: "Podcast video" },
  },
  pt: {
    homeTitle: "Planeje com quatro formatos comuns de vídeo",
    costLabel: "créditos estimados",
    outputLabel: "vídeos estimados",
    tableTitle: "Produção estimada de vídeos por plano",
    tableDescription: "Use os intervalos para comparar os planos com um formato de produção repetível.",
    profileLabel: "Perfil de produção",
    creditsLabel: "Créditos por vídeo",
    creditsUnit: "créditos",
    minutesShort: "min",
    visualsLabel: "visuais",
    estimateNote: "Modelo de voz, modelo visual, novas tentativas e densidade de cenas alteram o consumo real.",
    fallbackNote: "Os custos unitários usam temporariamente valores de referência porque a configuração ao vivo não estava disponível.",
    profiles: { short: "Vídeo curto sem rosto", explainer: "Vídeo explicativo educacional", "long-form": "Vídeo longo sem rosto", podcast: "Vídeo de podcast" },
  },
  es: {
    homeTitle: "Planifica con cuatro formatos de vídeo habituales",
    costLabel: "créditos estimados",
    outputLabel: "vídeos estimados",
    tableTitle: "Producción estimada de vídeos por plan",
    tableDescription: "Usa estos intervalos para comparar planes con un formato de producción repetible.",
    profileLabel: "Perfil de producción",
    creditsLabel: "Créditos por vídeo",
    creditsUnit: "créditos",
    minutesShort: "min",
    visualsLabel: "elementos visuales",
    estimateNote: "El modelo de voz, el modelo visual, los reintentos y la densidad de escenas cambian el consumo real.",
    fallbackNote: "Los costes unitarios usan temporalmente valores de referencia porque la configuración en vivo no estaba disponible.",
    profiles: { short: "Vídeo corto sin rostro", explainer: "Vídeo explicativo educativo", "long-form": "Vídeo largo sin rostro", podcast: "Vídeo de pódcast" },
  },
  de: {
    homeTitle: "Plane mit vier gängigen Videoformaten",
    costLabel: "geschätzte Credits",
    outputLabel: "geschätzte Videos",
    tableTitle: "Geschätzte Videomenge je Tarif",
    tableDescription: "Nutze diese Spannen, um Tarife für ein wiederholbares Produktionsformat zu vergleichen.",
    profileLabel: "Produktionsprofil",
    creditsLabel: "Credits pro Video",
    creditsUnit: "Credits",
    minutesShort: "Min.",
    visualsLabel: "Visuals",
    estimateNote: "Stimmenmodell, Bildmodell, Wiederholungen und Szenendichte verändern den tatsächlichen Verbrauch.",
    fallbackNote: "Die Stückkosten verwenden vorübergehend gekennzeichnete Ersatzwerte, da die Live-Konfiguration nicht verfügbar war.",
    profiles: { short: "Kurzes Video ohne Gesicht", explainer: "Lern-Erklärvideo", "long-form": "Langes Video ohne Gesicht", podcast: "Podcast-Video" },
  },
  fr: {
    homeTitle: "Planifiez avec quatre formats vidéo courants",
    costLabel: "crédits estimés",
    outputLabel: "vidéos estimées",
    tableTitle: "Production vidéo estimée par formule",
    tableDescription: "Utilisez ces fourchettes pour comparer les formules avec un format de production reproductible.",
    profileLabel: "Profil de production",
    creditsLabel: "Crédits par vidéo",
    creditsUnit: "crédits",
    minutesShort: "min",
    visualsLabel: "visuels",
    estimateNote: "Le modèle de voix, le modèle visuel, les nouvelles tentatives et la densité des scènes modifient le coût réel.",
    fallbackNote: "Les coûts unitaires utilisent temporairement des valeurs de référence signalées car la configuration en direct était indisponible.",
    profiles: { short: "Vidéo courte sans visage", explainer: "Vidéo explicative éducative", "long-form": "Vidéo longue sans visage", podcast: "Vidéo de podcast" },
  },
  ja: {
    homeTitle: "よく使う4つの動画形式で試算",
    costLabel: "推定クレジット",
    outputLabel: "推定動画本数",
    tableTitle: "プラン別の推定動画本数",
    tableDescription: "継続して制作する動画形式に合わせてプランを比較できます。",
    profileLabel: "制作形式",
    creditsLabel: "動画1本あたりのクレジット",
    creditsUnit: "クレジット",
    minutesShort: "分",
    visualsLabel: "ビジュアル",
    estimateNote: "音声モデル、画像モデル、再生成、シーン密度によって実際の消費量は変わります。",
    fallbackNote: "ライブ料金設定を取得できなかったため、明示した予備の単価を一時的に使用しています。",
    profiles: { short: "短尺の顔出しなし動画", explainer: "教育向け解説動画", "long-form": "長尺の顔出しなし動画", podcast: "ポッドキャスト動画" },
  },
  zh: {
    homeTitle: "按四种常见视频类型预估",
    costLabel: "预计积分",
    outputLabel: "预计视频数量",
    tableTitle: "各套餐预计视频产量",
    tableDescription: "用统一制作档案比较不同套餐可支持的重复生产量。",
    profileLabel: "制作档案",
    creditsLabel: "每条视频积分",
    creditsUnit: "积分",
    minutesShort: "分钟",
    visualsLabel: "画面",
    estimateNote: "声音模型、画面模型、重试次数和镜头密度会影响实际积分消耗。",
    fallbackNote: "实时价格配置暂时不可用，当前单位成本使用已明确标注的回退值。",
    profiles: { short: "短篇不露脸视频", explainer: "教育讲解视频", "long-form": "长篇不露脸视频", podcast: "播客视频" },
  },
  "zh-tw": {
    homeTitle: "依四種常見影片類型估算",
    costLabel: "預估點數",
    outputLabel: "預估影片數量",
    tableTitle: "各方案預估影片產量",
    tableDescription: "用一致的製作設定比較不同方案可支援的重複產量。",
    profileLabel: "製作設定",
    creditsLabel: "每支影片點數",
    creditsUnit: "點數",
    minutesShort: "分鐘",
    visualsLabel: "畫面",
    estimateNote: "語音模型、影像模型、重試次數與鏡頭密度會影響實際點數消耗。",
    fallbackNote: "即時價格設定暫時無法取得，目前單位成本使用已標示的備用值。",
    profiles: { short: "短篇不露臉影片", explainer: "教育解說影片", "long-form": "長篇不露臉影片", podcast: "Podcast 影片" },
  },
  ko: {
    homeTitle: "자주 쓰는 네 가지 영상 형식으로 예상",
    costLabel: "예상 크레딧",
    outputLabel: "예상 영상 수",
    tableTitle: "요금제별 예상 영상 제작량",
    tableDescription: "반복 제작할 영상 형식에 맞춰 요금제를 비교하세요.",
    profileLabel: "제작 프로필",
    creditsLabel: "영상당 크레딧",
    creditsUnit: "크레딧",
    minutesShort: "분",
    visualsLabel: "이미지",
    estimateNote: "음성 모델, 이미지 모델, 재시도 횟수와 장면 밀도에 따라 실제 사용량이 달라집니다.",
    fallbackNote: "실시간 가격 설정을 가져오지 못해 현재 단위 비용은 표시된 대체 값을 사용합니다.",
    profiles: { short: "짧은 얼굴 없는 영상", explainer: "교육 설명 영상", "long-form": "긴 얼굴 없는 영상", podcast: "팟캐스트 영상" },
  },
  nl: {
    homeTitle: "Plan met vier gangbare videoformaten",
    costLabel: "geschatte credits",
    outputLabel: "geschatte video's",
    tableTitle: "Geschatte videoproductie per abonnement",
    tableDescription: "Gebruik deze bandbreedtes om abonnementen voor een herhaalbaar productieformaat te vergelijken.",
    profileLabel: "Productieprofiel",
    creditsLabel: "Credits per video",
    creditsUnit: "credits",
    minutesShort: "min",
    visualsLabel: "beelden",
    estimateNote: "Stemmodel, beeldmodel, nieuwe pogingen en scènedichtheid veranderen het werkelijke verbruik.",
    fallbackNote: "De eenheidskosten gebruiken tijdelijk gemarkeerde terugvalwaarden omdat de live configuratie niet beschikbaar was.",
    profiles: { short: "Korte gezichtsloze video", explainer: "Educatieve uitlegvideo", "long-form": "Lange gezichtsloze video", podcast: "Podcastvideo" },
  },
  pl: {
    homeTitle: "Planuj z czterema popularnymi formatami wideo",
    costLabel: "szacowane kredyty",
    outputLabel: "szacowana liczba filmów",
    tableTitle: "Szacowana liczba filmów w planie",
    tableDescription: "Użyj tych zakresów, aby porównać plany dla powtarzalnego formatu produkcji.",
    profileLabel: "Profil produkcji",
    creditsLabel: "Kredyty na film",
    creditsUnit: "kredytów",
    minutesShort: "min",
    visualsLabel: "grafik",
    estimateNote: "Model głosu, model obrazu, ponowienia i gęstość scen zmieniają rzeczywiste zużycie.",
    fallbackNote: "Koszty jednostkowe tymczasowo używają oznaczonych wartości zapasowych, ponieważ konfiguracja na żywo była niedostępna.",
    profiles: { short: "Krótki film bez twarzy", explainer: "Edukacyjny film objaśniający", "long-form": "Długi film bez twarzy", podcast: "Film podcastowy" },
  },
  ru: {
    homeTitle: "Планирование для четырех популярных форматов",
    costLabel: "примерное число кредитов",
    outputLabel: "примерное число видео",
    tableTitle: "Примерный выпуск видео по тарифам",
    tableDescription: "Используйте диапазоны, чтобы сравнить тарифы для повторяемого формата производства.",
    profileLabel: "Профиль производства",
    creditsLabel: "Кредиты на видео",
    creditsUnit: "кредитов",
    minutesShort: "мин",
    visualsLabel: "визуальных кадров",
    estimateNote: "Модель голоса, модель изображений, повторы и плотность сцен меняют фактический расход.",
    fallbackNote: "Сейчас применяются отмеченные резервные цены, потому что рабочая конфигурация была недоступна.",
    profiles: { short: "Короткое видео без лица", explainer: "Обучающее видео", "long-form": "Длинное видео без лица", podcast: "Видео для подкаста" },
  },
};

export const getPricingProductionCopy = (locale: string) =>
  copies[locale] || copies.en;
